import { CalendarToday } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  Container,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import AppFooter from "../../components/footer";
import NavBar from "../../components/NavBar";
import { getBlogDetail } from "../../services/blogsFn";
import { decodeHtml } from "../../utils";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) {
    return "Today";
  } else if (diffDays === 2) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays - 1} days ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
};

const ALL_CATEGORY = [
  {
    title_en: "Ecommerce",
    title_ro: "Comerț electronic",
    type: "ecommerce",
  },
  {
    title_en: "Cleaning Service",
    title_ro: "Serviciu de curatenie",
    type: "cleaning",
  },
  {
    title_en: "Car Service",
    title_ro: "Service auto",
    type: "car",
  },
];

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  useEffect(() => {
    fetchBlogDetail();
  }, [slug]);

  const fetchBlogDetail = async () => {
    try {
      const response = await getBlogDetail(slug);
      if (response.data.status === "success") {
        setBlog(response.data.data);
      } else {
        setError(response.data.message);
      }
    } catch (error) {
      setError(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <NavBar />
        <Container sx={{ mt: 16, mb: 4 }}>
          <Box sx={{ maxWidth: 800, mx: "auto" }}>
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
          </Box>
        </Container>
        <AppFooter bgcolor={true} />
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavBar />
        <Container sx={{ mt: 16, mb: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
        <AppFooter bgcolor={true} />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Container sx={{ mt: 16, mb: 4 }}>
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* Blog Image */}
          <Box
            sx={{
              width: "100%",
              height: { xs: 220, sm: 320, md: 400 },
              borderRadius: 2,
              overflow: "hidden",
              mb: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <img
              src={blog?.image}
              alt={blog?.title[currentLanguage]}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              loading="lazy"
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ mb: 2, lineHeight: 1.2 }}
          >
            {blog?.title[currentLanguage]}
          </Typography>

          {/* Meta Info */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarToday sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {formatDate(blog?.createdAt)}
              </Typography>
            </Stack>

            {/* {blog?.content && (
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTime sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {calculateReadingTime(blog.content[currentLanguage])} min read
                </Typography>
              </Stack>
            )} */}
            {/* {blog?.author && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Person sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  {blog.author[currentLanguage]}
                </Typography>
              </Stack>
            )} */}

            {blog?.category && (
              <Chip
                label={
                  ALL_CATEGORY.find(
                    (category) => category.type === blog.category
                  )?.[`title_${currentLanguage}`]
                }
                size="small"
                sx={{
                  backgroundColor: "#f5f5f5",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                }}
              />
            )}
          </Stack>

          {/* Small Description */}
          {blog?.smallDescription && (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 3, fontStyle: "italic" }}
            >
              {blog.smallDescription[currentLanguage]}
            </Typography>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Blog Content (HTML) */}
          <Box
            sx={{
              fontSize: "1.1rem",
              color: "text.primary",
              lineHeight: 1.8,
              "& img": { maxWidth: "100%", borderRadius: 2, my: 2 },
              "& h1, & h2, & h3": { mt: 3, mb: 1 },
              "& ul, & ol": { pl: 3, mb: 2 },
              "& blockquote": {
                borderLeft: "4px solid #eee",
                pl: 2,
                color: "text.secondary",
                fontStyle: "italic",
                my: 2,
              },
            }}
            dangerouslySetInnerHTML={{
              __html: decodeHtml(blog?.description[currentLanguage]) || "",
            }}
          />
        </Box>
      </Container>
      <AppFooter bgcolor={true} />
    </>
  );
};

export default BlogDetail;
